/**
 * Polish (pl) translations.
 *
 * Same key shape as the Slovak source dictionary (`Dictionary` = `typeof sk`).
 * "Solandio" is treated as an indeclinable brand nominative — never declined.
 *
 * DUAL-MODE COPY: like the Slovak source, this dictionary ships BOTH the warm
 * family-mode wording and the co-parenting `*Separated` variants. The bare keys
 * default to the family-mode (warm) copy. ZERO legal vocabulary in family mode —
 * `sąd`, `opieka naprzemienna`, `przekazanie`, `porozumienie`, `50/50` appear
 * ONLY in the explicit `*Separated` strings.
 *
 * Do NOT change the `Dictionary` shape — only translate values.
 */
import { type Dictionary } from './sk';

export const pl: Dictionary = {
  appName: 'Solandio',
  tagline: 'Słoneczna strefa dla Waszych dzieci i rodziny.',

  language: {
    label: 'Język',
    select: 'Wybierz język',
  },

  common: {
    save: 'Zapisz',
    saving: 'Zapisywanie…',
    cancel: 'Anuluj',
    add: 'Dodaj',
    edit: 'Edytuj',
    delete: 'Usuń',
    remove: 'Usuń',
    close: 'Zamknij',
    loading: 'Wczytywanie…',
    none: 'Brak',
    optional: 'opcjonalne',
    back: 'Wstecz',
    today: 'Dziś',
    note: 'Notatka',
    required: 'To pole jest wymagane.',
    error: 'Coś poszło nie tak.',
    retry: 'Spróbuj ponownie',
    confirmDelete: 'Na pewno usunąć?',
    skipToContent: 'Przejdź do treści',
  },

  auth: {
    signInTitle: 'Witamy w Solandio',
    signInLead:
      'Spokojne, przejrzyste miejsce, w którym cała rodzina ma porządek — kalendarz, dzieci, zakupy i wydatki.',
    signInMicrosoft: 'Zaloguj się lub zarejestruj',
    signInGoogle: 'Zaloguj się przez Google',
    signInMock: 'Zaloguj się (środowisko lokalne)',
    signOut: 'Wyloguj się',
    signedInAs: 'Zalogowano jako',
    loadingSession: 'Weryfikujemy logowanie…',
    warmingUp:
      'Przygotowujemy Solandio… przy pierwszym otwarciu po dłuższej przerwie może to potrwać kilka sekund.',
    devNotice:
      'Lokalny tryb deweloperski — bez Azure. Dane są zapisane wyłącznie w Twojej przeglądarce.',
  },

  nav: {
    home: 'Start',
    children: 'Dzieci',
    contacts: 'Kontakty',
    health: 'Zdrowie',
    calendar: 'Kalendarz',
    shopping: 'Zakupy',
    documents: 'Dokumenty',
    messages: 'Wiadomości',
    expenses: 'Wydatki',
    agreements: 'Porozumienia',
    sharing: 'Rodzina',
    audit: 'Rejestr zmian',
    settings: 'Ustawienia',
    modules: 'Moduły',
    menu: 'Menu',
  },

  settings: {
    title: 'Ustawienia',
    lead: 'Zarządzaj swoim profilem, językiem i logowaniem.',
    profile: 'Profil',
    displayName: 'Wyświetlana nazwa',
    displayNameHint: 'Tę nazwę widzą inni członkowie rodziny.',
    email: 'E-mail',
    emailHint: 'Adresu e-mail nie można zmienić — jest powiązany z Twoim logowaniem.',
    nameInvalid: 'Podaj nazwę (1–120 znaków).',
    saved: 'Zapisano.',
    preferences: 'Preferencje',
    account: 'Konto',
    signOutHint: 'Wyloguj się z tego urządzenia.',
  },

  profileSetup: {
    title: 'Witamy w Solandio!',
    lead: 'Zanim zaczniemy, powiedz nam, jak mamy się do Ciebie zwracać.',
    nameLabel: 'Jak mamy się do Ciebie zwracać?',
    nameHint: 'To imię zobaczą inni członkowie rodziny. Możesz je zmienić w każdej chwili w Ustawieniach.',
    namePlaceholder: 'np. Jana',
    nameInvalid: 'Podaj imię (1–120 znaków).',
    submit: 'Kontynuuj',
  },

  family: {
    switcher: 'Rodzina',
    create: 'Utwórz rodzinę',
    createTitle: 'Utwórz swoją rodzinę',
    createLead:
      'Rodzina to Wasza wspólna przestrzeń — dla partnera, dzieci i dziadków. To Wy decydujecie, kto co widzi.',
    nameLabel: 'Nazwa rodziny',
    namePlaceholder: 'np. Nasza rodzina',
    empty: 'Nie masz jeszcze żadnej rodziny.',
  },

  home: {
    /** Greeting; {name} is the signed-in member's first name. */
    greetingMorning: 'Dzień dobry, {name}',
    greetingDay: 'Dzień dobry, {name}',
    greetingEvening: 'Dobry wieczór, {name}',
    /** Fallback greeting when no name is known yet. */
    greetingNeutral: 'Witaj w domu',
    add: 'Dodaj',
    /** Section titles on the at-a-glance dashboard. */
    todayTitle: 'Dziś',
    childrenTitle: 'Dzieci',
    shoppingTitle: 'Zakupy',
    expensesTitle: 'Wydatki domowe',
    lastNoteTitle: 'Ostatnia wiadomość',
    /** Small links/summaries under sections. */
    tomorrow: 'Jutro',
    tomorrowEvents: 'Jutro: {count} wydarzenia',
    viewAll: 'Pokaż wszystko',
    nothingToday: 'Dziś nic w kalendarzu.',
    noChildrenYet: 'Nie ma tu jeszcze żadnego dziecka.',
    noShoppingYet: 'Brak listy zakupów.',
    noExpensesYet: 'W tym miesiącu jeszcze brak wydatków.',
    noNotesYet: 'Jeszcze żadnych wiadomości.',
    thisMonth: 'Ten miesiąc',
    /** Expenses summary line, e.g. "3 nevyrovnané". */
    outstandingCount: '{count} nierozliczone',
    /** Empty state — brand-new family with nothing added yet. */
    emptyTitle: 'Witaj w domu 👋',
    emptyLead: 'Zacznij od trzech kroków i masz całą rodzinę w jednym miejscu.',
    emptyAddChild: 'Dodaj pierwsze dziecko',
    emptyInvitePartner: 'Zaproś partnera',
    emptyCreateList: 'Utwórz listę zakupów',
  },

  children: {
    title: 'Dzieci',
    add: 'Dodaj dziecko',
    addTitle: 'Dodaj dziecko',
    none: 'Nie ma tu jeszcze żadnego dziecka.',
    noneLead: 'Dodaj pierwsze dziecko — urodziny, zdrowie i kontakty będziesz mieć pod ręką.',
    nameLabel: 'Imię',
    namePlaceholder: 'np. Ema',
    emojiLabel: 'Awatar (emoji)',
    birthDateLabel: 'Data urodzenia',
    ageYears: 'lat',
    selectPrompt: 'Wybierz dziecko powyżej.',
    /** Kid-profile destination: labels for the per-child quick links. */
    profileTitle: 'Profil dziecka',
    profileLead: 'Wszystko o dziecku w jednym miejscu — jednym kliknięciem.',
    quickHealth: 'Zdrowie',
    quickContacts: 'Kontakty',
    quickCalendar: 'Kalendarz',
    quickDocuments: 'Dokumenty',
    birthday: 'Urodziny',
    ageLabel: 'Wiek',
    noBirthDate: 'Data urodzenia nie jest wypełniona.',
    editTitle: 'Edytuj dziecko',
    removeConfirm: 'Na pewno usunąć to dziecko? Jego zapisy pozostaną zachowane.',
  },

  contacts: {
    title: 'Kontakty',
    add: 'Dodaj kontakt',
    empty: 'Brak kontaktów.',
    typeLabel: 'Typ',
    labelLabel: 'Nazwa',
    labelPlaceholder: 'np. dr Nowak',
    phoneLabel: 'Telefon',
    addressLabel: 'Adres',
    types: {
      doctor: 'Lekarz',
      school: 'Szkoła',
      dentist: 'Dentysta',
      activity: 'Zajęcia',
      other: 'Inne',
    },
  },

  health: {
    title: 'Karta zdrowia',
    sensitive: 'Dane wrażliwe (RODO art. 9)',
    lead: 'Ważne informacje zdrowotne w jednym miejscu — na wypadek nagłej sytuacji.',
    allergies: 'Alergie',
    medications: 'Leki',
    insurance: 'Ubezpieczyciel',
    bloodType: 'Grupa krwi',
    conditions: 'Diagnozy / dolegliwości',
    notes: 'Notatki',
    updatedAt: 'Ostatnia zmiana',
    placeholderNone: 'Nie wypełniono',
  },

  calendar: {
    title: 'Kalendarz',
    add: 'Dodaj wydarzenie',
    empty: 'Brak wydarzeń.',
    upcoming: 'Najbliższe',
    titleLabel: 'Nazwa wydarzenia',
    typeLabel: 'Typ',
    startLabel: 'Początek',
    endLabel: 'Koniec',
    allDayLabel: 'Cały dzień',
    locationLabel: 'Miejsce',
    /** Separated-mode only: the custody sub-view tab label. */
    custodyView: 'Opieka naprzemienna',
    /**
     * Event-type labels. `types` is the family-mode default (no handover/custody
     * surfaced). `typesSeparated` adds the co-parenting types and is selected
     * when `family.mode === 'separated'`. Both carry every key so callers can
     * index either map by the same `CalendarEventType`.
     */
    types: {
      appointment: 'Termin',
      handover: 'Inne',
      custody: 'Inne',
      school: 'Szkoła',
      activity: 'Aktywność',
      other: 'Inne',
    },
    typesSeparated: {
      appointment: 'Termin',
      handover: 'Przekazanie',
      custody: 'Zmiana opieki',
      school: 'Szkoła',
      activity: 'Aktywność',
      other: 'Inne',
    },
  },

  custody: {
    title: 'Harmonogram opieki naprzemiennej',
    none: 'Harmonogram opieki naprzemiennej nie został jeszcze ustawiony.',
    patternLabel: 'Schemat',
    startLabel: 'Początek harmonogramu',
    notesLabel: 'Notatki',
    set: 'Ustaw harmonogram',
    update: 'Edytuj harmonogram',
    parentA: 'Rodzic A',
    parentB: 'Rodzic B',
    thisWeek: 'Ten tydzień',
    patterns: {
      'week-week': 'Tydzień / tydzień',
      '2-2-3': '2-2-3',
      custom: 'Własny',
    },
  },

  documents: {
    title: 'Dokumenty',
    sensitive: 'Wrażliwe',
    add: 'Dodaj dokument',
    empty: 'Brak dokumentów.',
    nameLabel: 'Nazwa',
    categoryLabel: 'Kategoria',
    fileLabel: 'Plik',
    fileHint: 'W trybie lokalnym plik nie jest przesyłany — zapisywane są tylko metadane.',
    uploadedBy: 'Dodał(a)',
    size: 'Rozmiar',
  },

  shopping: {
    title: 'Zakupy',
    /** List picker + creation. */
    listLabel: 'Lista',
    newList: 'Nowa lista',
    newListTitle: 'Nowa lista zakupów',
    listNameLabel: 'Nazwa listy',
    listNamePlaceholder: 'np. Spożywcze',
    renameList: 'Zmień nazwę listy',
    deleteList: 'Usuń listę',
    deleteListConfirm: 'Na pewno usunąć tę listę razem ze wszystkimi pozycjami?',
    noLists: 'Brak list.',
    noListsLead: 'Utwórz pierwszą listę — na przykład „Spożywcze” lub „Drogeria”.',
    /** Item add + list. */
    addItem: 'Dodaj pozycję',
    addItemPlaceholder: 'Dodaj pozycję…',
    itemNameLabel: 'Pozycja',
    empty: 'Lista jest pusta.',
    emptyLead: 'Dodaj pierwszą pozycję powyżej.',
    /** Category grouping (free-form label on each item). */
    categoryLabel: 'Kategoria',
    categoryNone: 'Bez kategorii',
    categoryPlaceholder: 'np. Owoce i warzywa',
    /** Assignee = the member responsible for buying the item. */
    assigneeLabel: 'Kto kupi',
    assigneeNone: 'Ktokolwiek',
    /** Buy-off. */
    bought: 'Kupione',
    markBought: 'Oznacz jako kupione',
    clearBought: 'Usuń kupione',
    /** {count} → number of checked items to clear. */
    clearBoughtCount: 'Usuń kupione ({count})',
    clearBoughtConfirm: 'Usunąć wszystkie kupione pozycje z tej listy?',
    itemCount: '{count} pozycji',
  },

  messages: {
    /**
     * "Wiadomości" in family mode (warm) / "Dziennik wiadomości" in separated
     * (court-log). Bare `title`/`lead`/`empty` default to the family wording; the
     * `*Family` and `*Separated` variants let the mode-aware UI pick explicitly.
     * Under the hood messages stay append-only in BOTH modes.
     */
    title: 'Wiadomości',
    titleFamily: 'Wiadomości',
    titleSeparated: 'Dziennik wiadomości',
    lead: 'Krótkie rodzinne wiadomości i notatki — na przykład „Odbierz Emę o 15:00”.',
    leadFamily: 'Krótkie rodzinne wiadomości i notatki — na przykład „Odbierz Emę o 15:00”.',
    leadSeparated:
      'Niezmienny, możliwy do wykorzystania przed sądem zapis komunikacji. Wiadomości nie można edytować ani usuwać.',
    placeholder: 'Napisz wiadomość…',
    placeholderSeparated: 'Napisz wiadomość…',
    send: 'Wyślij',
    empty: 'Jeszcze żadnych wiadomości. Napisz pierwszą.',
    emptyFamily: 'Jeszcze żadnych wiadomości. Napisz pierwszą.',
    emptySeparated: 'Dziennik jest jeszcze pusty. Napisz pierwszą wiadomość.',
    newest: 'Najnowsza',
    /** Shown only in separated mode (the append-only guarantee matters there). */
    appendOnlyNote: 'Tylko dopisywanie — bez edycji i usuwania.',
  },

  expenses: {
    /**
     * "Wydatki domowe" (household budgeting) in family mode vs "Wydatki" with
     * split/settle machinery in separated. Bare keys default to family wording;
     * `*Separated` variants carry the co-parenting framing.
     */
    title: 'Wydatki domowe',
    titleFamily: 'Wydatki domowe',
    titleSeparated: 'Wydatki',
    lead: 'Przegląd tego, kto co zapłacił — wspólny budżet domowy.',
    leadSeparated: 'Wspólne wydatki na dzieci i ich podział między rodziców.',
    add: 'Dodaj wydatek',
    empty: 'Brak wydatków.',
    amountLabel: 'Kwota',
    categoryLabel: 'Kategoria',
    descriptionLabel: 'Opis',
    childLabel: 'Dziecko',
    childAll: 'Cała rodzina',
    incurredOnLabel: 'Data',
    /** Split UI is hidden in family mode; these labels surface in separated. */
    splitLabel: 'Podział',
    split5050: 'Po połowie (50/50)',
    splitCustom: 'Własny',
    payerShare: 'Twój udział',
    otherShare: 'Udział drugiego rodzica',
    /** Family mode shows only "kto zapłacił" without any split. */
    paidBy: 'Kto zapłacił',
    paidByFamily: 'Kto zapłacił',
    settled: 'Rozliczone',
    outstanding: 'Nierozliczone',
    settle: 'Oznacz jako rozliczone',
    totalOutstanding: 'Nierozliczone razem',
    /** Family-mode month total (no settlement concept). */
    totalMonth: 'Razem w tym miesiącu',
    you: 'Ty',
  },

  agreements: {
    title: 'Porozumienia',
    lead:
      'Pisemne porozumienia między rodzicami. Jeden rodzic proponuje porozumienie, drugi je akceptuje lub odrzuca. Zaakceptowane porozumienie jest niezmiennym, możliwym do wykorzystania przed sądem zapisem.',
    propose: 'Zaproponuj porozumienie',
    proposeTitle: 'Zaproponuj porozumienie',
    proposeLead:
      'Sformułuj porozumienie jasno. Drugi rodzic będzie mógł je zaakceptować lub odrzucić.',
    proposeNote:
      'Po zaakceptowaniu porozumienie zostaje zablokowane i nie można go już edytować ani usuwać.',
    titleLabel: 'Nazwa porozumienia',
    titlePlaceholder: 'np. Wakacje letnie 2026',
    bodyLabel: 'Treść porozumienia',
    bodyPlaceholder: 'Opisz, na co się umówiliście…',
    empty: 'Brak porozumień.',
    emptyLead: 'Zaproponuj pierwsze porozumienie drugiemu rodzicowi.',
    proposedBy: 'Zaproponował(a)',
    respondedBy: 'Rozpatrzył(a)',
    accept: 'Zaakceptuj',
    decline: 'Odrzuć',
    withdraw: 'Wycofaj',
    locked: 'Zablokowane — bez dalszych zmian.',
    actionStale: 'Porozumienie zostało w międzyczasie rozpatrzone. Odświeżyliśmy listę.',
    you: 'Ty',
    status: {
      proposed: 'Zaproponowane',
      accepted: 'Zaakceptowane',
      declined: 'Odrzucone',
      withdrawn: 'Wycofane',
    },
  },

  sharing: {
    title: 'Rodzina',
    lead:
      'Zobacz, kto należy do rodziny, i zdecyduj, co każdy widzi. Babcię lub dziadka możesz zaprosić jako osobę zaufaną — zobaczą tylko to, na co pozwolisz.',
    familyNameLabel: 'Nazwa rodziny',
    rename: 'Zmień nazwę',
    renameTitle: 'Zmień nazwę rodziny',
    members: 'Członkowie rodziny',
    invite: 'Zaproś',
    inviteMember: 'Zaproś członka',
    inviteTitle: 'Zaproś członka rodziny',
    emailLabel: 'E-mail',
    roleLabel: 'Rola w rodzinie',
    roleParent: 'Rodzic',
    roleParentDesc: 'Pełny dostęp do wszystkiego — dla drugiego rodzica.',
    roleTrusted: 'Osoba zaufana',
    roleTrustedDesc:
      'Na przykład babcia lub dziadek — widzą tylko to, co zaznaczysz.',
    scopesLabel: 'Co może widzieć',
    scopesHint:
      'Rodzic widzi wszystko. Osoba zaufana widzi tylko zaznaczone obszary.',
    parentScopesNote:
      'Rodzic automatycznie otrzymuje pełny dostęp do wszystkich obszarów.',
    sensitiveNote: 'Wrażliwe — udzielane tylko wyraźnie.',
    seesLabel: 'Widzi',
    seesNothing: 'Nie ma jeszcze dostępu do żadnego obszaru.',
    fullAccess: 'Pełny dostęp',
    status: {
      invited: 'Zaproszony(a)',
      active: 'Aktywny(a)',
    },
    you: 'Ty',
    parentBadge: 'Rodzic',
    trustedBadge: 'Osoba zaufana',
    changeScopes: 'Edytuj dostęp',
    removeMember: 'Usuń',
    removeConfirm: 'Czy na pewno usunąć tę osobę z rodziny?',
    onlyParents: 'Tylko rodzice mogą zarządzać członkami rodziny i ich dostępem.',
  },

  scopes: {
    children: 'Dzieci',
    calendar: 'Kalendarz',
    contacts: 'Kontakty',
    health: 'Zdrowie',
    documents: 'Dokumenty',
    messages: 'Wiadomości',
    expenses: 'Wydatki',
    shopping: 'Zakupy',
  },

  mode: {
    /** Settings → "Tryb rodziny" card. Parent-only, lives at the bottom. */
    cardTitle: 'Tryb rodziny',
    cardLead:
      'Solandio jest ustawione dla rodziny, która mieszka razem. Jeśli mieszkacie w osobnych gospodarstwach domowych, możecie przełączyć tryb — wszystkie Wasze dane i cała historia dzieci pozostają nienaruszone.',
    /** Current-mode labels. */
    familyLabel: 'Wspólne gospodarstwo domowe',
    familyDesc: 'Kalendarz, dzieci, zakupy i wydatki dla całej rodziny.',
    separatedLabel: 'Osobne gospodarstwa domowe',
    separatedDesc:
      'Dodaje opiekę naprzemienną, podział wydatków, porozumienia i dziennik wiadomości.',
    currentLabel: 'Aktualny tryb',
    /** Switch actions. */
    switchToSeparated: 'Przełącz na osobne gospodarstwa domowe',
    switchToFamily: 'Przełącz z powrotem na wspólne gospodarstwo domowe',
    /** Two-step confirmation dialog (deliberate friction). */
    confirmToSeparatedTitle: 'Przełączyć na tryb dla osobnych gospodarstw domowych?',
    confirmToSeparatedLead:
      'Dodamy narzędzia dla rodziców, którzy mieszkają osobno — opiekę naprzemienną, podział wydatków, porozumienia i dziennik wiadomości.',
    confirmToSeparatedNote:
      'Wszystkie Wasze dane i cała historia dzieci pozostają nienaruszone. Tryb możesz w każdej chwili przełączyć z powrotem.',
    confirmToSeparatedAction: 'Tak, przełącz',
    confirmToFamilyTitle: 'Przełączyć z powrotem na wspólne gospodarstwo domowe?',
    confirmToFamilyLead:
      'Narzędzia dla osobnych gospodarstw domowych zostaną ukryte. Zapisy pozostają zachowane — przy następnym przełączeniu znajdziesz je takimi, jakie były.',
    confirmToFamilyAction: 'Tak, przełącz z powrotem',
    /** Shown while the change is being saved + after it lands. */
    switching: 'Przełączam tryb…',
    switched: 'Tryb został zmieniony.',
    /** Gate copy when a non-parent somehow reaches the card. */
    onlyParents: 'Tryb rodziny mogą zmieniać tylko rodzice.',
  },

  audit: {
    title: 'Rejestr zmian',
    lead: 'Kto, co i kiedy zmienił. Zapis jest niezmienny (tylko dopisywanie).',
    empty: 'Brak zapisów.',
    onlyParents: 'Rejestr zmian widzą tylko rodzice.',
  },

  errors: {
    forbidden: 'Nie masz uprawnień do tego obszaru.',
    notFound: 'Nie znaleziono pozycji.',
    generic: 'Akcja nie powiodła się. Spróbuj ponownie.',
  },

  tour: {
    /** Re-invoke action (Settings + help button in the top bar). */
    start: 'Uruchom przewodnik',
    help: 'Przewodnik',
    helpHint: 'Krótkie wprowadzenie do aplikacji możesz odtworzyć w każdej chwili.',
    /** Joyride control labels. */
    back: 'Wstecz',
    next: 'Dalej',
    last: 'Gotowe',
    skip: 'Pomiń',
    close: 'Zamknij',
    /** "Krok 2 z 6" — {step} a {steps} doplní Joyride. */
    progress: 'Krok {step} z {steps}',
    steps: {
      welcomeTitle: 'Witamy w Solandio',
      welcome: 'Za kilka sekund wszystko pokażemy.',
      childrenTitle: 'Dzieci',
      children: 'Tutaj dodasz dzieci i przełączasz się między nimi.',
      modulesTitle: 'Wszystko o rodzinie',
      modules:
        'Po lewej znajdziesz wszystko dla rodziny — kalendarz, dzieci, zdrowie, zakupy, wydatki, dokumenty i wiadomości.',
      familyTitle: 'Rodzina',
      family:
        'Tutaj zaprosisz partnera lub babcię/dziadka i ustalisz, co widzą.',
      settingsTitle: 'Ustawienia',
      settings: 'W Ustawieniach zmienisz imię i język.',
      finishTitle: 'Gotowe!',
      finish: 'Zacznij od dodania dziecka lub listy zakupów.',
    },
  },
} as const;
